/**
 * RoleBasedRedirect Component
 * Redirects users to their appropriate dashboard based on role
 */

import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { ROUTES } from '@/constants/routes';
import { Loader2 } from 'lucide-react';

/**
 * Redirects authenticated users to their role-specific dashboard
 */
export function RoleBasedRedirect() {
  const { isAuthenticated, loading, getDefaultRoute } = useAuth();

  if (loading) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to={ROUTES.LOGIN} replace />;
  }

  return <Navigate to={getDefaultRoute()} replace />;
}

/**
 * UnauthorizedPage - Shown when user doesn't have access
 */
export function UnauthorizedPage() {
  const { getDefaultRoute, isAuthenticated } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to={ROUTES.LOGIN} replace />;
  }

  return (
    <div className="flex h-screen w-full flex-col items-center justify-center bg-background">
      <div className="text-center space-y-4">
        <h1 className="text-4xl font-bold text-foreground">403</h1>
        <h2 className="text-xl text-muted-foreground">Access Denied</h2>
        <p className="text-sm text-muted-foreground">
          You don't have permission to access this page.
        </p>
        <a 
          href={getDefaultRoute()} 
          className="inline-block mt-4 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
        >
          Go to Dashboard
        </a>
      </div>
    </div>
  );
}

/**
 * NotFoundPage - Shown for unknown routes
 */
export function NotFoundPage() {
  const { getDefaultRoute, isAuthenticated } = useAuth();

  return (
    <div className="flex h-screen w-full flex-col items-center justify-center bg-background">
      <div className="text-center space-y-4">
        <h1 className="text-4xl font-bold text-foreground">404</h1>
        <h2 className="text-xl text-muted-foreground">Page Not Found</h2>
        <p className="text-sm text-muted-foreground">
          The page you're looking for doesn't exist.
        </p>
        <a 
          href={isAuthenticated ? getDefaultRoute() : ROUTES.LOGIN} 
          className="inline-block mt-4 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
        >
          {isAuthenticated ? 'Go to Dashboard' : 'Go to Login'}
        </a>
      </div>
    </div>
  );
}

export default RoleBasedRedirect;
